import { useState, useEffect } from 'react';
import { Menu, X, Calendar } from 'lucide-react';
import { cn } from '../lib/utils';
import { publicApi, getImageUrl } from '../lib/api';

const baseNavLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Therapies', href: '#therapies' },
  { label: 'Prices', href: '#prices' },
  { label: 'Vouchers', href: '#vouchers', requires: 'vouchers' },
  { label: 'Contact', href: '#contact' },
];

export default function Header({ onBookClick, bookingEnabled }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState('/images/logo.png');
  const [vouchersEnabled, setVouchersEnabled] = useState(false);
  const navLinks = baseNavLinks.filter((link) => link.requires !== 'vouchers' || vouchersEnabled);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    
    // Load logo from settings
    publicApi.getSettings().then(res => {
      if (res.data.settings?.images?.logo_url) {
        setLogoUrl(res.data.settings.images.logo_url);
      }
      const gv = res.data.settings?.gift_voucher_settings;
      setVouchersEnabled(!!(gv?.enabled && gv?.sumup_link));
    }).catch(() => {});
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-white/80 backdrop-blur-sm'
      )}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => scrollToSection(e, '#home')}
            className="flex items-center"
            data-testid="header-logo"
          >
            <img
              src={getImageUrl(logoUrl)}
              alt="White Dove Wellness"
              className="h-16 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-sm font-medium uppercase tracking-widest text-slate-600 hover:text-[#9F87C4] transition-colors relative after:content-[''] after:absolute after:left-0 after:bottom-[-4px] after:w-0 after:h-[2px] after:bg-[#9F87C4] after:transition-all hover:after:w-full"
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            {bookingEnabled && (
              <button
                onClick={onBookClick}
                className="bg-[#9F87C4] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#8A6EB5] transition-all flex items-center gap-2"
                data-testid="header-book-btn"
              >
                <Calendar size={16} />
                Book Now
              </button>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-600 hover:text-[#9F87C4]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-100" data-testid="mobile-menu">
          <nav className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="block py-2 text-sm font-medium uppercase tracking-widest text-slate-600 hover:text-[#9F87C4]"
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            {bookingEnabled && (
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onBookClick?.();
                }}
                className="w-full bg-[#9F87C4] text-white px-5 py-3 rounded-full text-sm font-medium hover:bg-[#8A6EB5] transition-all flex items-center justify-center gap-2 mt-4"
                data-testid="mobile-book-btn"
              >
                <Calendar size={16} />
                Book Now
              </button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
